// DXF Unit Detection - Package 9-10.
// Pure functions, no side effects, fully unit-testable in isolation.
// This module owns the ONLY logic that inspects $INSUNITS - nothing
// else in the codebase should read dxf.header directly.

// Official Autodesk DXF $INSUNITS code table (subset with real
// conversion factors to meters). Codes not listed here (rare units
// like Astronomical Units, Light Years, Parsecs) are treated as
// 'unsupported' rather than crashing or guessing.
export const DXF_UNIT_TO_METERS = {
  1: 0.0254,     // Inches
  2: 0.3048,     // Feet
  3: 1609.344,   // Miles
  4: 0.001,      // Millimeters
  5: 0.01,       // Centimeters
  6: 1.0,        // Meters
  7: 1000.0,     // Kilometers
  9: 0.0000254,  // Mils
  10: 0.9144,    // Yards
  13: 0.000001,  // Microns
  14: 0.1,       // Decimeters
  15: 10.0,      // Decameters
  16: 100.0,     // Hectometers
}

export const DXF_UNIT_LABELS = {
  1: 'Inches', 2: 'Feet', 3: 'Miles', 4: 'Millimeters', 5: 'Centimeters',
  6: 'Meters', 7: 'Kilometers', 9: 'Mils', 10: 'Yards', 13: 'Microns',
  14: 'Decimeters', 15: 'Decameters', 16: 'Hectometers',
}

// Inspects a parsed dxf-parser result and returns the detection
// outcome WITHOUT throwing and WITHOUT ever assuming meters.
// Returns: { status: 'detected', code, factorToMeters, label }
//        | { status: 'unknown' }  -- header missing, $INSUNITS absent,
//                                    $INSUNITS === 0 (explicit Unitless),
//                                    or an $INSUNITS code we don't
//                                    recognize/support a factor for.
export function detectDxfUnit(parsedDxf) {
  const code = parsedDxf && parsedDxf.header ? parsedDxf.header['$INSUNITS'] : undefined
  if (code === undefined || code === null) return { status: 'unknown' } // header missing OR $INSUNITS absent entirely
  if (code === 0) return { status: 'unknown' } // explicit Unitless per DXF spec
  const factorToMeters = DXF_UNIT_TO_METERS[code]
  if (factorToMeters === undefined) return { status: 'unknown' } // recognized-but-unsupported or invalid code
  return { status: 'detected', code: code, factorToMeters: factorToMeters, label: DXF_UNIT_LABELS[code] }
}

// Given a resolved DXF unit code (either auto-detected or the code
// the user manually selected) and the target unit the user's
// Calibration record will use, returns the exact {pdf_distance,
// real_distance} pair to store - reusing the EXISTING calibration
// contract unchanged. pdf_distance=1 represents exactly one raw DXF
// coordinate unit (an honest, real value - not an artificial one).
const METERS_PER_TARGET_UNIT = { m: 1, cm: 0.01, mm: 0.001, ft: 0.3048, in: 0.0254 }

export function buildDxfCalibration(dxfUnitCode, targetUnit) {
  const dxfFactorToMeters = DXF_UNIT_TO_METERS[dxfUnitCode]
  const targetFactorToMeters = METERS_PER_TARGET_UNIT[targetUnit]
  if (dxfFactorToMeters === undefined || targetFactorToMeters === undefined) return null
  return {
    pdf_distance: 1,
    real_distance: dxfFactorToMeters / targetFactorToMeters, // exact conversion, never approximate
    unit: targetUnit,
  }
}
